import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { BlogEnglishNotice } from '@/components/BlogEnglishNotice';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, User, Shield, Lock, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogGDPRCopilot = () => {
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    
    if (currentPath === '/' || currentPath === '/fr') {
      const ctaSection = document.getElementById('cta');
      if (ctaSection) {
        ctaSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
      window.location.href = homeUrl;
    }
  };

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "The Best GDPR-Compliant Salesforce AI Copilot (Built in Europe)",
    "description": "Need a Salesforce AI Copilot that's GDPR-first and EU-based? Oli anonymises sensitive data before LLM prompts and writes natively to any Salesforce object/field.",
    "image": "https://noq5.ai/images/oli-gdpr-copilot.png",
    "author": {
      "@type": "Organization",
      "name": "NoQ5.ai Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "NoQ5.ai",
      "logo": {
        "@type": "ImageObject",
        "url": "https://noq5.ai/images/noq5-logo.png"
      }
    },
    "datePublished": "2025-01-17",
    "dateModified": "2025-01-17",
    "mainEntityOfPage": "https://noq5.ai/blog/gdpr-compliant-salesforce-ai-copilot-europe"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://noq5.ai/blog" },
      { "@type": "ListItem", "position": 3, "name": "The Best GDPR-Compliant Salesforce AI Copilot (Built in Europe)", "item": "https://noq5.ai/blog/gdpr-compliant-salesforce-ai-copilot-europe" }
    ]
  };

  return (
    <Layout>
      <SEOHead 
        title="EU GDPR-Compliant Salesforce & HubSpot AI Copilot (Built in Ireland)"
        description="Only EU-based AI for Salesforce & HubSpot with automatic PII anonymization. Irish data center, GDPR-first architecture. Quick setup. Try free 30 days."
        canonicalUrl="https://noq5.ai/blog/gdpr-compliant-salesforce-ai-copilot-europe"
        jsonLd={[blogPostingSchema, breadcrumbSchema]}
      />
      <div className="container px-4 md:px-6 py-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center text-oli-blue hover:text-oli-blue-dark mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          <BlogEnglishNotice />

          <div className="mb-8">
            <div className="inline-block rounded-lg bg-oli-blue-light px-3 py-1 text-sm text-oli-blue mb-4">
              GDPR & Privacy
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              The Best GDPR-Compliant Salesforce AI Copilot (Built in Europe)
            </h1>
            <div className="flex items-center text-gray-500 space-x-4 text-sm">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                NoQ5.ai Team
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                8 min read
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              European businesses need Salesforce AI copilots that comply with GDPR while maintaining data sovereignty. Many US-based solutions create compliance headaches with data transfers, vendor assessments, and unclear privacy practices. <Link to="/app" className="text-oli-blue hover:text-oli-blue-dark underline">Oli is built differently</Link> — EU-based in Ireland with privacy-by-design that anonymises sensitive data before any LLM processing.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">EU Data Concerns with AI Copilots</h2>
            <p className="mb-6">
              When implementing Salesforce AI copilots, European companies face several compliance challenges:
            </p>

            <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-8">
              <h4 className="font-semibold text-lg mb-3 text-red-800">Common GDPR Risks:</h4>
              <ul className="space-y-2 text-red-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3"></div>
                  <div><strong>Data residency requirements:</strong> Customer data processed outside the EU</div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3"></div>
                  <div><strong>Vendor risk assessments:</strong> Complex due diligence for US providers</div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3"></div>
                  <div><strong>Data Processing Impact Assessments (DPIAs):</strong> Required for AI processing</div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3"></div>
                  <div><strong>Subject access rights:</strong> Difficulty fulfilling data portability requests</div>
                </li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Why US Copilots Can Be Tricky for GDPR</h2>
            
            <p className="mb-6">
              Many popular Salesforce AI copilots are US-based, creating potential compliance gaps:
            </p>

            <div className="space-y-6 mb-8">
              <div className="border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-lg mb-2 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-yellow-600" />
                  Salesforce Agentforce/Einstein
                </h4>
                <p className="text-gray-600 mb-2">
                  While Salesforce offers EU data centers, Agentforce AI processing may still involve US infrastructure for certain capabilities.
                </p>
                <p className="text-sm text-gray-500">
                  <strong>GDPR consideration:</strong> Requires careful review of data flows and processing locations.
                </p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-lg mb-2 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-yellow-600" />
                  Microsoft Copilot
                </h4>
                <p className="text-gray-600 mb-2">
                  Microsoft has EU data centers but data processing for AI features often involves multiple regions.
                </p>
                <p className="text-sm text-gray-500">
                  <strong>GDPR consideration:</strong> Complex data transfer mechanisms and varying service-specific terms.
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Oli's GDPR-First Approach</h2>
            
            <div className="bg-gradient-to-r from-green-50 to-oli-blue-light/20 p-8 rounded-lg mb-8">
              <h3 className="text-2xl font-semibold text-oli-blue mb-6 flex items-center">
                <Lock className="w-6 h-6 mr-2" />
                Built for European Privacy Standards
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Ireland-based infrastructure:</strong> EU data residency with no US transfers
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Anonymisation before LLM processing:</strong> Sensitive fields replaced with tokens
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Least-privilege API access:</strong> Only necessary Salesforce permissions
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Audit trails for write-backs:</strong> Full transparency on CRM updates
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Role-based permissions alignment:</strong> Respects your org's sharing rules
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Privacy-by-design architecture:</strong> Data protection built into core functions
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">How Oli's Anonymisation Works</h2>
            
            <p className="mb-6">
              Here's how Oli protects sensitive data while maintaining functionality:
            </p>

            <div className="space-y-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-oli-blue" />
                  Example: Opportunity Update
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <strong>User says:</strong> "Just had a call with Acme Corp. They're interested but want to see pricing for 500 users. Expected close date is March 15th."
                  </div>
                  <div>
                    <strong>Oli processes:</strong> "Just had a call with [ACCOUNT_TOKEN_1]. They're interested but want to see pricing for [NUMBER_TOKEN_1] users. Expected close date is [DATE_TOKEN_1]."
                  </div>
                  <div>
                    <strong>Result:</strong> Salesforce opportunity updated with actual values, but LLM never sees "Acme Corp" or specific details.
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-oli-blue" />
                  Example: Contact Notes
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <strong>User says:</strong> "Met John Smith from TechStart. He mentioned budget concerns and wants to discuss implementation timeline."
                  </div>
                  <div>
                    <strong>Oli processes:</strong> "Met [CONTACT_TOKEN_1] from [COMPANY_TOKEN_1]. They mentioned budget concerns and wants to discuss implementation timeline."
                  </div>
                  <div>
                    <strong>Result:</strong> Activity logged to correct contact record with full context, but personal details never exposed to AI processing.
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Transparent Roadmap Note</h2>
            <p className="mb-6">
              We believe in transparency about our current capabilities and future plans:
            </p>
            <div className="bg-oli-blue-light/20 p-6 rounded-lg mb-8">
              <p className="mb-4">
                <strong>Available today:</strong> Voice and chat-based Salesforce updates with full GDPR compliance and EU data residency.
              </p>
              <p className="mb-4">
                <strong>Coming soon:</strong> Email and calendar integrations are on our roadmap. These will maintain the same privacy-first approach with EU-based processing.
              </p>
              <p className="text-sm text-gray-600">
                Want to influence our roadmap? <Link to="/app" className="text-oli-blue hover:text-oli-blue-dark underline">Join our waitlist</Link> and let us know what integrations matter most to your team.
              </p>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Getting Started with GDPR Compliance</h2>
            
            <p className="mb-6">
              Ready to try a Salesforce AI copilot that puts European privacy first?
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-oli-blue text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-bold">1</div>
                <h4 className="font-semibold mb-2">Connect Sandbox</h4>
                <p className="text-sm text-gray-600">Start with your Salesforce sandbox for risk-free testing</p>
              </div>
              <div className="text-center p-6 border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-oli-blue text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-bold">2</div>
                <h4 className="font-semibold mb-2">Test Updates</h4>
                <p className="text-sm text-gray-600">Try voice and chat updates with anonymised processing</p>
              </div>
              <div className="text-center p-6 border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-oli-blue text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-bold">3</div>
                <h4 className="font-semibold mb-2">Production Ready</h4>
                <p className="text-sm text-gray-600">Deploy with confidence knowing your GDPR requirements are met</p>
              </div>
            </div>

            <div className="bg-oli-blue text-white p-8 rounded-lg text-center mb-12">
              <h3 className="text-2xl font-bold mb-4">Try NoQ5.ai with Your Salesforce Sandbox</h3>
              <p className="mb-6">
                Experience GDPR-compliant Salesforce automation built in Europe for European businesses.
              </p>
              <Button 
                onClick={scrollToDemo}
                className="bg-white text-oli-blue hover:bg-gray-100"
              >
                Try NoQ5 Now
              </Button>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">Where is Oli's infrastructure located?</h3>
                <p className="text-gray-600">
                  Oli is hosted in Ireland (EU) with all data processing and storage within EU borders. We do not transfer data to the US or other non-EU regions.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">How does anonymisation work with complex Salesforce data?</h3>
                <p className="text-gray-600">
                  Oli identifies and tokenises sensitive fields (names, companies, emails, phone numbers) before AI processing. The original values are securely mapped back when writing to Salesforce, ensuring accuracy without exposing personal data to LLMs.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">Do you provide Data Processing Agreements (DPAs)?</h3>
                <p className="text-gray-600">
                  Yes, we provide comprehensive DPAs that meet GDPR requirements, including details on data processing purposes, categories of data, retention periods, and technical/organisational measures.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">How does Oli compare to US-based copilots for GDPR compliance?</h3>
                <p className="text-gray-600">
                  US-based copilots often require complex adequacy decisions, standard contractual clauses, or supplementary measures. Oli eliminates these complications by operating entirely within the EU with privacy-by-design architecture.
                </p>
              </div>
            </div>

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">Related Resources</h3>
              <div className="space-y-2">
                <p><Link to="/blog/best-salesforce-ai-copilot-smbs-2025" className="text-oli-blue hover:text-oli-blue-dark underline">Best CRM AI Copilot for SMBs (2025)</Link></p>
                <p><Link to="/blog/update-salesforce-automatically-2025" className="text-oli-blue hover:text-oli-blue-dark underline">How to Update Salesforce Automatically Without Manual Entry</Link></p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default BlogGDPRCopilot;